import { NavLink, useMatch } from "react-router-dom";
import { Inbox, Kanban, CalendarDays, BarChart2, Users, MessageCircle, Building2 } from "lucide-react";
import { useCurrentUserProfile } from "@/hooks/useUsers";
import { useClientSwitcherStore } from "@/stores/client-switcher.store";
import { useClientFeatures } from "@/hooks/useClientFeatures";
import { FEATURE_KEYS } from "@school-sales-ai/shared";

const CLIENTS_TAB = { to: "/clients", label: "Clientes", Icon: Building2 } as const;

// "Agente" and "Plantillas" are deliberately absent here while present in the
// sidebar. With Buzón added, an admin would carry 7 flex-1 tabs — about 51px each
// on a 360px phone, too narrow to hit reliably, and an 8th would make every one
// of them worse. Both are configuration surfaces nobody drives from a phone:
// writing a template body with variables and examples is desk work. The Buzón is
// the opposite. Their plan gates still apply in the sidebar and server-side.
const BASE_TABS = [
  { to: "/buzon",          label: "Buzón",    Icon: Inbox        },
  { to: "/pipeline",       label: "Prospectos", Icon: Kanban     },
  { to: "/visitas",        label: "Visitas",  Icon: CalendarDays },
  { to: "/reports",        label: "Reportes", Icon: BarChart2    },
] as const;

const ADMIN_TABS = [
  { to: "/users",              label: "Equipo",    Icon: Users         },
  { to: "/whatsapp-connect",   label: "WhatsApp",  Icon: MessageCircle },
] as const;

export function BottomNav(): React.JSX.Element | null {
  const isDetail = useMatch("/leads/:id");
  const profile = useCurrentUserProfile();
  const activeClientId = useClientSwitcherStore((s) => s.activeClientId);
  const { data: features } = useClientFeatures();

  if (isDetail) return null;

  const isAdmin = profile?.role === "admin";
  const isSuperAdmin = profile?.role === "super_admin";
  const isAccountManager = profile?.role === "account_manager";
  // Account managers get the admin tabs on the same terms super-admins do:
  // once a school is selected. The sidebar already gates them this way.
  const showAdminTabs = isAdmin || ((isSuperAdmin || isAccountManager) && !!activeClientId);
  // Visitas is gated on the active client's plan feature (mirrors the sidebar).
  const canVisits = (features ?? []).includes(FEATURE_KEYS.visits);

  const tabs = [
    ...(isAccountManager ? [CLIENTS_TAB] : []),
    ...BASE_TABS.filter((tab) => tab.to !== "/visitas" || canVisits),
    ...(showAdminTabs ? ADMIN_TABS : []),
  ];

  return (
    <div
      className="md:hidden shrink-0 bg-white border-t border-ink-100 flex items-stretch"
      style={{ height: 64, paddingBottom: 8 }}
    >
      {tabs.map(({ to, label, Icon }) => (
        <NavLink
          key={to}
          to={to}
          className={({ isActive }) =>
            `flex-1 flex flex-col items-center justify-center gap-[3px] pt-1.5 ${
              isActive ? "text-moss" : "text-ink-400"
            }`
          }
        >
          {({ isActive }) => (
            <>
              <Icon size={20} strokeWidth={isActive ? 2.2 : 1.7} />
              <span
                className={`text-[10px] leading-none ${isActive ? "font-semibold" : "font-normal"}`}
              >
                {label}
              </span>
            </>
          )}
        </NavLink>
      ))}
    </div>
  );
}
